import { useCallback, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { FxAlert, FxButton, FxIcon, FxPanel, FxSpinner } from '@/components/fx'
import { adminApi, ApiError } from '@/lib/api'

import {
  HEALTH_COMPONENTS,
  STATUS_TONE,
  componentLine,
  componentOf,
  componentStatus,
  formatStand,
  overallSummary,
  type SystemHealth,
} from './health'
import { HEALTH_INTERVAL_MS, POLL_BUDGET_MS, pollInterval } from './jobs'

const BANNER_TONE = {
  healthy: 'bg-success-subtle',
  degraded: 'bg-warning-subtle',
  unhealthy: 'bg-danger-subtle',
  unknown: 'bg-fill',
} as const

/**
 * "System": the gateway's seven health checks, one line each.
 *
 * What is ported from the retired admin build's `SystemHealth.tsx` is the
 * query and nothing else — its four cards were keyed on a `firebase`
 * component the gateway no longer reports, its metric tables printed raw
 * snake_case keys, and its overall badge was the only thing between an
 * operator and a wall of numbers. The German, the per-component lines and the
 * banner are new.
 */
export function SystemPanel() {
  // The poll's own clock. A fetch every 30s is cheap, but a settings tab left
  // open for a week is not, so the budget runs from the last time a person
  // asked for this screen — mount, or the refresh button.
  const [startedAt, setStartedAt] = useState(() => Date.now())
  const startedAtRef = useRef(startedAt)
  startedAtRef.current = startedAt

  const query = useQuery<SystemHealth>({
    queryKey: ['verwaltung', 'health'],
    queryFn: () => adminApi.getDetailedHealth() as Promise<SystemHealth>,
    // A health screen that retries silently behind a spinner hides exactly the
    // outage it exists to show.
    retry: false,
    refetchInterval: () =>
      pollInterval({
        active: true,
        elapsedMs: Date.now() - startedAtRef.current,
        intervalMs: HEALTH_INTERVAL_MS,
      }),
  })

  const refresh = useCallback(() => {
    setStartedAt(Date.now())
    void query.refetch()
  }, [query])

  const paused = Date.now() - startedAt >= POLL_BUDGET_MS
  const health = query.data
  const summary = overallSummary(query.isError ? undefined : health)
  const stand = formatStand(health?.timestamp)

  return (
    <FxPanel
      title="System"
      action={
        <button
          type="button"
          aria-label="Systemzustand neu prüfen"
          onClick={refresh}
          disabled={query.isFetching}
          className="inline-flex h-control w-control items-center justify-center rounded-md text-text-muted hover:bg-fill disabled:text-text-disabled"
        >
          <FxIcon name="refresh" size={20} className={query.isFetching ? 'animate-spin' : ''} />
        </button>
      }
    >
      {query.isLoading ? (
        <FxSpinner label="Systemzustand wird geprüft …" />
      ) : query.isError ? (
        <div className="flex flex-col gap-4">
          <FxAlert tone="danger" title="Systemzustand nicht abrufbar">
            <p>
              {ApiError.from(query.error).message ||
                'Die Abfrage des Systemzustands ist fehlgeschlagen.'}
            </p>
            <p>
              Was Sie hier sehen würden, ist damit unbekannt — nicht in Ordnung. Prüfen Sie es
              erneut oder sehen Sie im Cluster nach.
            </p>
          </FxAlert>
          <div>
            <FxButton variant="outlined" icon="refresh" onClick={refresh}>
              Erneut prüfen
            </FxButton>
          </div>
        </div>
      ) : (
        <div className="flex flex-col">
          <div
            className={`mb-2 flex items-center gap-3 rounded-sm p-4 ${BANNER_TONE[summary.status]}`}
          >
            <FxIcon
              name={STATUS_TONE[summary.status].icon}
              size={20}
              className={`shrink-0 ${STATUS_TONE[summary.status].className}`}
            />
            <p className="text-small text-text">
              <b>{summary.word}</b> – {summary.detail}
            </p>
          </div>

          <ul className="m-0 list-none p-0">
            {HEALTH_COMPONENTS.map(({ key, name }, index) => {
              const component = componentOf(health, key)
              const status = component ? componentStatus(component.status) : 'unknown'
              const tone = STATUS_TONE[status]
              return (
                <li
                  key={key}
                  className={`flex items-start gap-3 py-[14px] ${
                    index < HEALTH_COMPONENTS.length - 1 ? 'border-b-2 border-divider' : ''
                  }`}
                >
                  <FxIcon name={tone.icon} size={20} className={`mt-0.5 shrink-0 ${tone.className}`} />
                  <span className="min-w-0 flex-grow">
                    <b className="text-body">{name}</b>
                    <br />
                    <span className="text-small text-text-muted">{componentLine(key, component)}</span>
                    {/* The gateway's own sentence, only where something is
                        wrong: it names the cause, and it is written for the
                        person who has to go and fix it.
                        
                        Labelled, because it is English in a German screen —
                        the API's messages are factual and this panel phrases
                        everything else itself. Without the label
                        "No active converter detected" reads as a translation
                        somebody forgot rather than as the system talking. This
                        panel is admin-only, so the raw sentence is worth more
                        than a tidier screen. */}
                    {status !== 'healthy' && component?.message ? (
                      <>
                        <br />
                        <span className="break-words text-caption text-text-muted">
                          Systemmeldung: {component.message}
                        </span>
                      </>
                    ) : null}
                  </span>
                </li>
              )
            })}
          </ul>

          <p className="mt-4 text-small text-text-muted">
            {stand ?? 'Für diese Prüfung liegt kein Zeitstempel vor.'}
          </p>
          {paused && (
            <p className="mt-1 text-small text-text-muted">
              Die automatische Aktualisierung ist pausiert. Prüfen Sie erneut, um sie fortzusetzen.
            </p>
          )}
        </div>
      )}
    </FxPanel>
  )
}
